import { frameAABB, type ConnectorFrame } from "@refinement-canvas/domain";
import { ZOOM_MAX, ZOOM_MIN, type ViewState } from "./canvas-coordinates";
import { deriveViewFromCenterStage } from "./canvas-view-url";

// キャンバス内容（オブジェクト群）を画面中央へ収める初期表示（view）を求めるユーティリティ。
// 参照専用（Connect Note の iframe 埋め込み）の初回表示で、左上原点でも「広大なキャンバスの
// 幾何中心」でもなく、実際に図形が存在する範囲の中心を画面中央へ合わせるために使う。

/** ステージ座標系での矩形（left/top/right/bottom）。 */
export interface ContentBounds {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/**
 * すべてのオブジェクトの外接矩形（ステージ座標）を求める。
 * オブジェクトが 1 つも無い、または座標が不正で矩形を決められない場合は null を返す。
 * ペンストロークを含む全オブジェクトが x/y/w/h の枠を持つ前提（domain の ConnectorFrame）。
 * 回転（rotation）を持つオブジェクトは、実際の描画が中心まわりに `transform: rotate` されるため、
 * 未回転の x/y/w/h ではなく回転後の外接矩形（frameAABB）で評価する（回転後にはみ出す角まで収める）。
 */
export function computeObjectsBounds(
  objects: Record<string, ConnectorFrame>
): ContentBounds | null {
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const object of Object.values(objects)) {
    if (
      !Number.isFinite(object.x) ||
      !Number.isFinite(object.y) ||
      !Number.isFinite(object.w) ||
      !Number.isFinite(object.h)
    ) {
      continue;
    }
    // rotation を考慮した実表示の外接矩形（未回転・rotation 不正時は元の x/y/w/h をそのまま返す）。
    const aabb = frameAABB(object);
    left = Math.min(left, aabb.x);
    top = Math.min(top, aabb.y);
    right = Math.max(right, aabb.x + aabb.w);
    bottom = Math.max(bottom, aabb.y + aabb.h);
  }
  if (
    !Number.isFinite(left) ||
    !Number.isFinite(top) ||
    !Number.isFinite(right) ||
    !Number.isFinite(bottom)
  ) {
    return null;
  }
  return { left, top, right, bottom };
}

/**
 * 内容の外接矩形をビューポート中央へ収める view を返す。
 * - 中心は外接矩形の中心。
 * - ズームは「余白（padding）を残して収まる倍率」と maxZoom の小さい方。小さな内容を過剰に
 *   拡大しないよう maxZoom で頭打ちにし、内容がビューポートより大きいときだけ縮小する。
 *   最終的に ZOOM_MIN..ZOOM_MAX へクランプする。
 */
export function deriveFitToContentView(
  bounds: ContentBounds,
  viewport: { readonly width: number; readonly height: number },
  options: { readonly maxZoom: number; readonly padding?: number | undefined }
): ViewState {
  const centerX = (bounds.left + bounds.right) / 2;
  const centerY = (bounds.top + bounds.bottom) / 2;
  const contentW = bounds.right - bounds.left;
  const contentH = bounds.bottom - bounds.top;
  const padding = options.padding ?? 0.9;
  // 幅・高さそれぞれが正のときだけ収まり倍率を候補に入れる（点・水平/垂直線など片方が 0 でも扱える）。
  const zoomCandidates: number[] = [];
  if (contentW > 0) zoomCandidates.push((viewport.width * padding) / contentW);
  if (contentH > 0) zoomCandidates.push((viewport.height * padding) / contentH);
  const fitZoom = zoomCandidates.length > 0 ? Math.min(...zoomCandidates) : options.maxZoom;
  const zoom = clampZoom(Math.min(fitZoom, options.maxZoom));
  return deriveViewFromCenterStage(centerX, centerY, zoom, viewport);
}

function clampZoom(zoom: number): number {
  if (!Number.isFinite(zoom) || zoom <= 0) return ZOOM_MIN;
  return Math.min(Math.max(zoom, ZOOM_MIN), ZOOM_MAX);
}
